import { NextPageContext } from "next";
import clsx from "clsx";
import { useEffect, useRef } from "react";
import Image from "next/image";
import Head from "next/head";
import { useWindowSize } from "@/components/WindowSizeProvider";

import { initializeAnalytics } from "@/utils/analytics";
import Link from "@/components/hoc/CustomLink";

interface Props {
  path?: string;
  err?: (Error & { statusCode?: number }) | null;
}

export default function Error({ path, err }: Props) {
  const { width } = useWindowSize();
  const isMobile = width < 1024;
  const hasTrackedError = useRef(false);

  useEffect(() => {
    // Remove header and footer because error page does not support i18n
    const footer = document.querySelector("footer");
    if (footer) footer.remove();
    const header = document.querySelector("header");
    if (header) header.remove();
  }, []);

  useEffect(() => {
    if (hasTrackedError.current) return;
    hasTrackedError.current = true;
    initializeAnalytics().then((analytics) => {
      analytics?.track("WEB - Global - Error Page - Displayed", {
        path,
        statusCode: err?.statusCode,
        errorName: err?.name,
        errorMessage: err?.message,
        errorCause: String(err?.cause),
      });
    });
  }, [path, err]);

  return (
    <div className="fixed inset-0 w-screen h-screen z-99999">
      <Head>
        <title>Error - Pianity</title>
      </Head>
      <div
        className={clsx(
          "absolute w-screen h-full top-0 left-0 -z-5",
          "bg-dark-3 bg-dark-bg-mobile lg:bg-dark-bg"
        )}
      />
      <div className="absolute w-screen top-4 lg:top-14 text-white">
        <div className="container flex items-baseline justify-between lg:justify-start">
          <Link href="/">
            <a>
              <Image
                src="/assets/logo/pianity-white.svg"
                alt="logo"
                className="cursor-pointer"
                width={isMobile ? 140 : 170}
                height={isMobile ? 18 : 22}
              />
            </a>
          </Link>
        </div>
      </div>
      <div className="container flex pt-28 sm:pt-0 items-end h-full w-full lg:pb-0 rounded-md">
        <div
          className={clsx(
            "w-full h-full px-10 lg:px-0 lg:h-auto flex flex-col",
            "lg:flex-row lg:items-end justify-center lg:justify-between",
            "lg:mb-20"
          )}
        >
          <div className="flex flex-col">
            <div className={"text-60 lg:text-7xl text-white font-medium"}>Woops</div>
            <div
              className={clsx(
                "text-20 lg:text-22 leading-0.75 font-medium tracking-wider",
                "text-purple-3 mt-8 lg:mt-20 mb-1 lg:mb-2"
              )}
            >
              Unexpected Error
            </div>
            <div className={"text-lg lg:text-2xl text-white"}>Something went wrong.</div>
          </div>
          <div className="text-white flex flex-col lg:flex-row lg:items-end mt-14 sm:mt-0">
            <Link href="/">
              <a
                className={clsx(
                  "text-white text-sm lg:text-base transition",
                  "ease-quart-out duration-300 bg-gradient-selected",
                  "md:hover:shadow-button rounded-full px-4 h-10 lg:px-6",
                  "lg:h-12 flex items-center w-max uppercase font-medium",
                  "tracking-wider cursor-pointer"
                )}
              >
                Back to home
              </a>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

Error.getInitialProps = async (ctx: NextPageContext) => {
  return {
    props: {
      path: ctx.asPath,
      err: ctx.err,
    },
  };
};
